import { useEffect, useState } from "react";
import { Loader2, Download, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fileUrl } from "@/lib/api";
import { eur, fmtDate } from "@/lib/format";

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-sm font-medium">{value || "—"}</p>
  </div>
);

export default function FatturaPreviewModal({ tx, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open && tx) {
      setLoading(true); setErr(null); setData(null);
      api.get(`/transactions/${tx.id}/fattura-preview`)
        .then(setData)
        .catch((e) => setErr(e.message || "Errore nel caricamento."))
        .finally(() => setLoading(false));
    }
  }, [open, tx]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="fattura-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4" /> Anteprima Fattura</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : err ? (
          <p className="py-8 text-center text-sm text-destructive">{err}</p>
        ) : data ? (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 rounded-lg border border-border bg-secondary/40 p-4">
              <Field label="Numero" value={data.numero} />
              <Field label="Data" value={data.data ? fmtDate(data.data) : null} />
              <Field label="Tipo doc." value={data.tipo_documento} />
              <Field label="Totale" value={data.totale != null ? eur(data.totale) : null} />
              <div className="col-span-2"><Field label="Fornitore / Cedente" value={data.cedente_nome} /></div>
              <div className="col-span-2"><Field label="Cliente / Cessionario" value={data.cessionario_nome} /></div>
            </div>

            <div>
              <p className="text-sm font-semibold mb-2">Righe</p>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/60 text-xs text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">Descrizione</th>
                      <th className="text-right font-medium px-3 py-2 w-16">Q.tà</th>
                      <th className="text-right font-medium px-3 py-2 w-16">IVA%</th>
                      <th className="text-right font-medium px-3 py-2 w-28">Importo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.righe.length === 0 ? (
                      <tr><td colSpan={4} className="px-3 py-3 text-center text-muted-foreground text-xs">Nessuna riga di dettaglio.</td></tr>
                    ) : data.righe.map((r, i) => (
                      <tr key={i} data-testid={`preview-riga-${i}`}>
                        <td className="px-3 py-2">{r.descrizione || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{r.quantita ?? "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{r.aliquota != null ? `${r.aliquota}%` : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{r.prezzo_totale != null ? eur(r.prezzo_totale) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div className="space-y-2 text-sm">
                <p className="text-sm font-semibold">Riepilogo IVA</p>
                {data.riepilogo.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nessun riepilogo.</p>
                ) : data.riepilogo.map((r, i) => (
                  <div key={i} className="flex gap-4 text-xs text-muted-foreground" data-testid={`preview-iva-${i}`}>
                    <span>Aliquota {r.aliquota != null ? `${r.aliquota}%` : "—"}</span>
                    <span>Imponibile {r.imponibile != null ? eur(r.imponibile) : "—"}</span>
                    <span>Imposta {r.imposta != null ? eur(r.imposta) : "—"}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-lg bg-secondary/60 px-4 py-3 text-right min-w-[180px]">
                <div className="flex justify-between gap-6 text-xs text-muted-foreground"><span>Imponibile</span><span className="font-mono">{data.imponibile != null ? eur(data.imponibile) : "—"}</span></div>
                <div className="flex justify-between gap-6 text-xs text-muted-foreground"><span>IVA</span><span className="font-mono">{data.imposta != null ? eur(data.imposta) : "—"}</span></div>
                <div className="flex justify-between gap-6 text-sm font-bold mt-1 pt-1 border-t border-border"><span>Totale</span><span className="font-mono">{data.totale != null ? eur(data.totale) : "—"}</span></div>
              </div>
            </div>

            {tx?.allegato_file_id && (
              <a href={fileUrl(tx.allegato_file_id, true)} target="_blank" rel="noreferrer" data-testid="preview-download-xml"
                className="inline-flex items-center gap-1.5 text-xs text-amber-600 hover:underline">
                <Download className="h-3.5 w-3.5" /> Scarica XML originale ({tx.allegato_nome})
              </a>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
